<template>
  <section
    class="bg-gradient-to-r from-gray-100/70 to-gray-100 relative py-16 sm:py-24"
  >
    <div class="container relative">
      <div
        class="header-container flex justify-between items-center mb-8 bg-white p-6 rounded-lg shadow-sm"
      >
        <h1 class="text-3xl font-semibold text-gray-800">Destek Taleplerim</h1>
        <button
          class="bg-primary text-white px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
          @click="showCreateTicketModal = true"
        >
          <i class="fas fa-plus"></i> Yeni Talep
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
      <div class="flex flex-col items-center">
        <i class="fas fa-spinner fa-spin text-5xl text-primary mb-4"></i>
        <p class="text-gray-600">Talepleriniz yükleniyor...</p>
      </div>
    </div>

    <!-- Create Ticket Modal -->
    <div v-if="showCreateTicketModal" class="modal">
      <div class="modal-content">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-semibold">Yeni Talep Oluştur</h3>
          <button
            @click="showCreateTicketModal = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <input
          v-model="newTicket.subject"
          placeholder="Talep Konusu"
          class="modal-input"
        />
        <textarea
          v-model="newTicket.description"
          placeholder="Talep Açıklaması"
          class="modal-input"
          rows="4"
        ></textarea>
        <div class="modal-buttons">
          <button
            class="bg-red-500 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
            @click="showCreateTicketModal = false"
          >
            İptal
          </button>
          <button
            class="bg-green-500 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
            @click="handleCreateTicket"
          >
            Oluştur
          </button>
        </div>
      </div>
    </div>

    <!-- Existing tickets list -->
    <div v-if="!loading" class="container relative">
      <!-- Empty state when no tickets -->
      <div
        v-if="tickets.length === 0"
        class="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-10 min-h-[400px]"
      >
        <img
          :src="require('@/assets/ticket-empty-state.png')"
          alt="Destek talebi yok"
          class="w-32 h-32 mb-6"
        />
        <h3 class="text-xl font-semibold text-gray-800 mb-2">
          Henüz Destek Talebiniz Bulunmuyor
        </h3>
        <p class="text-gray-600 mb-6 text-center max-w-md">
          Herhangi bir sorunuz veya probleminiz mi var? Hemen yeni bir destek
          talebi oluşturabilirsiniz.
        </p>
        <button
          @click="showCreateTicketModal = true"
          class="bg-primary text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
        >
          <i class="fas fa-plus"></i> Yeni Talep Oluştur
        </button>
      </div>

      <!-- Tickets list -->
      <div v-else class="grid gap-4">
        <div
          v-for="ticket in tickets"
          :key="ticket.Id"
          class="bg-white p-6 mt-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-100"
          @click="goToDetails(ticket.Id)"
        >
          <div class="flex justify-between items-center">
            <div class="space-y-2">
              <h3 class="text-lg font-medium">{{ ticket.Subject }}</h3>
              <div class="flex items-center gap-3">
                <span :class="getStatusBadgeClass(ticket.TicketStatus)">
                  {{ getStatusText(ticket.TicketStatus) }}
                </span>
                <span class="text-gray-500 text-sm flex items-center gap-1">
                  <i class="far fa-clock text-xs"></i>
                  {{ formatDate(ticket.UpdatedAt) }}
                </span>
              </div>
            </div>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { getUserTickets, createTicket } from "@/services/ticketServices";
import { Toast } from "@/services/toastService";

const router = useRouter();
const tickets = ref([]);
const loading = ref(true);
const showCreateTicketModal = ref(false);
const newTicket = ref({
  subject: "",
  description: "",
});

const fetchTickets = async () => {
  try {
    const response = await getUserTickets();

    if (response.Success) {
      tickets.value = response.Payload;
    }
  } catch (error) {
    console.error("Ticket fetch error:", error);
  } finally {
    loading.value = false;
  }
};

const goToDetails = (ticketId) => {
  router.push(`/user/tickets/${ticketId}`);
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString("tr-TR");
};

const handleCreateTicket = async () => {
  if (!newTicket.value.subject.trim() || !newTicket.value.description.trim()) {
    Toast("Hata", "Lütfen tüm alanları doldurun", "error");
    return;
  }

  // check ıf subject and description are  has more than 5 characters
  if (newTicket.value.subject.length < 3) {
    Toast("Hata", "Talep konusu en az 5 karakter olmalıdır", "error");
    return;
  }
  if (newTicket.value.description.length < 10) {
    Toast("Hata", "Talep açıklaması en az 10 karakter olmalıdır", "error");
    return;
  }

  try {
    const response = await createTicket(
      newTicket.value.subject,
      newTicket.value.description
    );

    if (response.Success) {
      Toast("Başarılı", "Talep başarıyla oluşturuldu", "success");
      showCreateTicketModal.value = false;
      newTicket.value = { subject: "", description: "" }; // Reset form
      await fetchTickets(); // Refresh ticket list
    } else {
      Toast("Hata", "Talep oluşturulurken bir hata oluştu", "error");
    }
  } catch (error) {
    Toast("Hata", "Talep oluşturulurken bir hata oluştu", "error");
    console.error("Create ticket error:", error);
  }
};

const getStatusText = (status) => {
  const statusMap = {
    PENDING_ANSWER: "Cevap Bekleniyor",
    ANSWERED: "Cevaplandı",
    CLOSED: "Kapandı",
  };
  return statusMap[status] || status;
};

const getStatusBadgeClass = (status) => {
  const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
  const statusClasses = {
    PENDING_ANSWER: "bg-gray-100 text-gray-800",
    ANSWERED: "bg-green-100 text-green-800",
    CLOSED: "bg-red-100 text-red-800",
  };
  return `${baseClasses} ${statusClasses[status]}`;
};

onMounted(() => {
  fetchTickets();
});
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
}

.modal-input {
  width: 100%;
  margin: 10px 0;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

textarea.modal-input {
  resize: vertical;
  min-height: 100px;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}
</style>
